import { css, html, LitElement, unsafeCSS } from 'lit';
import styles from './fileUploader.scss?inline';
import { ref, createRef } from 'lit/directives/ref.js';
import { ATTRIBUTES, BYTES_IN_KB, BYTES_IN_MB } from './constants';
import messages from './localization.json';
import { getMessage } from '@/localization.js';
import { map } from 'lit/directives/map.js';

export default class SotbitFileUploader extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static formAssociated = true;

    static properties = {
        label: { type: String },
        name: { type: String },
        disabled: { type: Boolean },
        multiple: { type: Boolean },
        inputCaption: { type: String, attribute: ATTRIBUTES.INPUT_CAPTION },
        maxSize: { type: String, attribute: ATTRIBUTES.MAX_SIZE },
        value: { type: Object },
    };

    constructor() {
        super();

        this._internals = this.attachInternals();
        this.label = '';
        this.name = 'file';
        this.disabled = false;
        this.inputCaption = getMessage(messages, 'selectFile');
        this.maxSize = '';
        this.multiple = false;
        this._hiddenInputRef = createRef();
        const dt = new DataTransfer();
        this._files = dt.files;
        this._value = '';
    }

    get type() {
        return 'file';
    }

    get value() {
        return this._hiddenInputRef?.value ? this._hiddenInputRef?.value.value : this._value;
    }

    set value(value) {
        this._value = value;

        if (this._hiddenInputRef?.value) {
            this._hiddenInputRef.value.value = value;
        }
    }

    get files() {
        return this._hiddenInputRef?.value ? this._hiddenInputRef.value.files : this._files;
    }

    set files(files) {
        if (Array.isArray(files)) {
            const dt = new DataTransfer();
            files.forEach((file) => dt.items.add(file));
            files = dt.files;
        }

        this._files = files;

        if (this._hiddenInputRef?.value) {
            this._hiddenInputRef.value.files = files;
        }

        if (!this._files.length) {
            this.value = '';
        }
    }

    formResetCallback() {
        const dt = new DataTransfer();
        this.files = dt.files;
        this._internals.setFormValue(null);

        if (this._hiddenInputRef.value) {
            this._hiddenInputRef.value.value = '';
        }

        this.requestUpdate();
    }

    updateFormValue() {
        if (this.multiple) {
            const formData = new FormData();
            Array.from(this.files).forEach((file) => {
                formData.append(this.name, file);
            });
            this._internals.setFormValue(formData);
        } else {
            const file = this.files[0] || null;
            this._internals.setFormValue(file);
        }
    }

    handleFileChange() {
        this.updateFormValue();
        this.requestUpdate();
    }

    handleDelete(fileToRemove) {
        const dt = new DataTransfer();

        for (let i = 0; i < this.files.length; i++) {
            const file = this.files[i];
            if (file !== fileToRemove) {
                dt.items.add(file);
            }
        }

        this.files = dt.files;
        this.updateFormValue();
        this.requestUpdate();
    }

    formatSize(bytes) {
        if (bytes < BYTES_IN_KB) {
            return bytes + ' B';
        }

        if (bytes < BYTES_IN_MB) {
            return (bytes / BYTES_IN_KB).toFixed(1) + ' KB';
        }

        return (bytes / BYTES_IN_MB).toFixed(1) + ' MB';
    }

    _triggerFileDialog() {
        if (this._hiddenInputRef.value && !this.disabled) {
            this._hiddenInputRef.value.click();
        }
    }

    render() {
        return html`
            <div class="file-uploader">
                ${this.label ? html`<p class="file-uploader__title">${this.label}</p>` : ''}
                <div class="file-uploader__input-area">
                    <sotbit-button
                        variant="secondary"
                        background
                        outline
                        inline
                        @click=${this._triggerFileDialog.bind(this)}
                        ?disabled=${this.disabled}
                    >
                        <span slot="content">${this.inputCaption}</span>
                    </sotbit-button>
                    <input
                        type="file"
                        name=${this.name}
                        ?disabled=${this.disabled}
                        ?multiple=${this.multiple}
                        @change=${this.handleFileChange}
                        style="display: none"
                        ${ref(this._hiddenInputRef)}
                    />
                </div>

                ${this.files.length
                    ? html`
                          <div class="file-uploader__list">
                              ${map(Array.from(this.files), (file) => {
                                  const isImage = file.raw?.type?.startsWith('image/');
                                  return html`
                                      <div class="file-item">
                                          ${isImage
                                              ? html`<img
                                                    class="file-item__preview"
                                                    src=${file.url}
                                                    alt=${file.name}
                                                />`
                                              : html`
                                                    <div class="file-item__icon">
                                                        <sotbit-icon icon="docs"></sotbit-icon>
                                                    </div>
                                                `}
                                          <span class="file-item__name">${file.name}</span>
                                          <span class="file-item__size">${this.formatSize(file.size || 0)}</span>
                                          <sotbit-button-icon
                                              variant="secondary"
                                              size="s"
                                              background
                                              @click=${() => this.handleDelete(file)}
                                          >
                                              <sotbit-icon slot="content" icon="close" no-padding></sotbit-icon>
                                          </sotbit-button-icon>
                                      </div>
                                  `;
                              })}
                          </div>
                      `
                    : ''}
                ${this.maxSize ? html`<span class="file-uploader__max-size">${this.maxSize}</span>` : ''}
            </div>
        `;
    }
}
